import React from "react";
import { QuantityDiscount } from "~/ProductDetails/ProductDetailPageResult";
import { PricingProduct } from "../PricingProduct";
import QuantityDiscountCard from "./Components/QuantityDiscountCard";
import QuantityDiscountsTableCard from "./Components/QuantityDiscountsTableCard";
import getMinBuyForQuantityDropdown from "../AddToCart/getMinBuyForQuantityDropdown";

interface QuantityDiscountSectionProps {
  product: PricingProduct;
  quantityDiscounts: QuantityDiscount[];
  displayPrice: number;
  hasLotDiscounts: boolean;
  showCrossoutPrice: boolean;
}

const QuantityDiscountSectionV2 = ({
  product,
  quantityDiscounts,
  displayPrice,
  hasLotDiscounts,
  showCrossoutPrice,
}: QuantityDiscountSectionProps): JSX.Element | null => {
  const updatedMinBuy = getMinBuyForQuantityDropdown({
    minBuy: product.minimumBuy,
    mustBuy: product.mustBuy,
  });
  const multipleDiscountLevels = quantityDiscounts.length > 1;
  const minOrStartBuy = Math.max(updatedMinBuy, product.startBuy);
  const discountStartQuantity = quantityDiscounts?.[0]?.startQuantity ?? 2;
  let basePriceHeaderText = "";
  let discountHeaderText = "";

  if (discountStartQuantity === 2) {
    basePriceHeaderText = "Buy 1";
    discountHeaderText = "Buy 2 & Save";
  } else if (discountStartQuantity > 2 && minOrStartBuy <= 1) {
    basePriceHeaderText = `Buy 1 - ${discountStartQuantity - 1}`;
    discountHeaderText = `Buy ${discountStartQuantity} & Save`;
  } else if (discountStartQuantity - 1 <= minOrStartBuy) {
    basePriceHeaderText = `Buy ${minOrStartBuy}`;
    discountHeaderText = `Buy ${discountStartQuantity} & Save`;
  } else {
    basePriceHeaderText = `Buy ${minOrStartBuy} - ${discountStartQuantity - 1}`;
    discountHeaderText = `Buy ${discountStartQuantity} & Save`;
  }

  if (hasLotDiscounts && !showCrossoutPrice) {
    basePriceHeaderText = "Regularly";
  }

  const getContainerClassName = () => {
    if (quantityDiscounts.length === 2) {
      return " flex-row h-auto space-x-2 md:flex-col md:space-y-2 md:space-x-0 xxl:flex-row xxl:space-x-2 xxl:space-y-0";
    }
    if (quantityDiscounts.length > 2) {
      return " flex-col space-y-2 h-auto";
    }
    return " flex-row space-x-2 h-auto";
  };

  return quantityDiscounts[0] ? (
    <div
      className={`flex mb-3 mt-0 md:mt-2-1/4 ${getContainerClassName()}`}
      data-testid="quantity-discounts-section-v2"
    >
      <QuantityDiscountCard
        price={displayPrice}
        unitOfMeasure={product.unitOfMeasure}
        divideByUnit={product.divideByUnit}
        divideQuantity={product.divideQuantity}
        headerText={basePriceHeaderText}
        headerTextClassName={`text-sm leading-4 ${quantityDiscounts.length > 1 ? "font-semibold" : "font-bold"}`}
        multipleDiscountLevels={multipleDiscountLevels}
      />
      {quantityDiscounts.length > 1 ? (
        <QuantityDiscountsTableCard
          product={product}
          quantityDiscounts={quantityDiscounts}
        />
      ) : (
        <QuantityDiscountCard
          price={quantityDiscounts[0].price}
          unitOfMeasure={product.unitOfMeasure}
          divideByUnit={product.divideByUnit}
          divideQuantity={product.divideQuantity}
          isDiscount
          headerText={discountHeaderText}
          headerTextClassName="text-sm leading-4 bg-yellow-400 rounded-sm px-1 py-px w-fit font-bold"
        />
      )}
    </div>
  ) : null;
};

export default QuantityDiscountSectionV2;
